import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import '@/i18n';
import { AuthProvider } from '@/context/AuthContext';
import { SwitchProvider } from '@/context/SwitchContext';
import { useLenis } from '@/hooks/useLenis';
import { Layout } from '@/components/Layout';
import { ROUTE_SLUGS } from '@/i18n';

import Home from '@/pages/Home';
import About from '@/pages/About';
import Help from '@/pages/Help';
import HowItWorks from '@/pages/HowItWorks';
import Stories from '@/pages/Stories';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import Collaborate from '@/pages/Collaborate';
import Contact from '@/pages/Contact';
import Admin from '@/pages/Admin';
import AuthCallback from '@/pages/AuthCallback';

const PAGES = {
  home: Home, about: About, help: Help, how: HowItWorks, stories: Stories,
  blog: Blog, collaborate: Collaborate, contact: Contact,
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function buildRoutes() {
  const routes = [];
  ['it', 'en'].forEach((lang) => {
    Object.keys(PAGES).forEach((key) => {
      const slug = ROUTE_SLUGS[key][lang];
      const path = slug ? `/${lang}/${slug}` : `/${lang}`;
      const Comp = PAGES[key];
      routes.push(<Route key={`${lang}-${key}`} path={path} element={<Comp />} />);
    });
    routes.push(<Route key={`${lang}-blogpost`} path={`/${lang}/blog/:slug`} element={<BlogPost />} />);
  });
  return routes;
}

function LangRedirect() {
  const location = useLocation();
  const lang = location.pathname.startsWith('/en') ? 'en' : 'it';
  return <Navigate to={`/${lang}`} replace />;
}

function AppInner() {
  const location = useLocation();
  useLenis();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Navigate to="/it" replace />} />
        <Route path="/admin" element={<Admin />} />
        <Route element={<Layout />}>
          {buildRoutes()}
        </Route>
        <Route path="*" element={<LangRedirect />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <SwitchProvider>
              <AppInner />
              <Toaster position="top-center" richColors />
            </SwitchProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
